import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import {
  useCreateAdminSystemTableColumn,
  useUpdateAdminSystemTableColumn,
  useDeleteAdminSystemTableColumn
} from 'api/hooks/use-admin-system-table-columns';
import { useAdminSystems } from 'api/hooks/use-admin-systems';

const SystemTableColumnModal = ({ show, onHide, systemTableColumn, tableColumnId, filterType }) => {
  const isEditing = !!systemTableColumn;
  const { data: systems } = useAdminSystems();

  // Filter types that support options
  const supportsOptions = ['select', 'multiselect', 'reference'].includes(filterType);

  const createMutation = useCreateAdminSystemTableColumn({
    tableColumnId,
    options: {
      onSuccess: () => onHide()
    }
  });
  const updateMutation = useUpdateAdminSystemTableColumn({
    id: systemTableColumn?.id,
    options: {
      onSuccess: () => onHide()
    }
  });
  const deleteMutation = useDeleteAdminSystemTableColumn({
    options: {
      onSuccess: () => onHide()
    }
  });

  const [formData, setFormData] = useState({
    system_id: '',
    table_column_id: tableColumnId || '',
    display_name: '',
    custom_options: false,
    source_table: '',
    source_column: ''
  });

  useEffect(() => {
    if (systemTableColumn) {
      setFormData({
        system_id: systemTableColumn.system_id || '',
        table_column_id: systemTableColumn.table_column_id || '',
        display_name: systemTableColumn.display_name || '',
        custom_options: systemTableColumn.custom_options || false,
        source_table: systemTableColumn.source_table || '',
        source_column: systemTableColumn.source_column || ''
      });
    } else {
      setFormData({
        system_id: '',
        table_column_id: tableColumnId || '',
        display_name: '',
        custom_options: false,
        source_table: '',
        source_column: ''
      });
    }
  }, [systemTableColumn, tableColumnId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this system table column?')) {
      deleteMutation.mutate(systemTableColumn.id);
    }
  };

  const error = createMutation.error || updateMutation.error || deleteMutation.error;
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit System Table Column' : 'Create System Table Column'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error.message || 'An error occurred'}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>System *</Form.Label>
            <Form.Select
              name="system_id"
              value={formData.system_id}
              onChange={handleChange}
              required
              disabled={isEditing}
            >
              <option value="">Select a system...</option>
              {systems?.map((system) => (
                <option key={system.id} value={system.id}>
                  {system.name}
                </option>
              ))}
            </Form.Select>
            {isEditing && (
              <Form.Text className="text-muted">
                System cannot be changed after creation
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Display Name *</Form.Label>
            <Form.Control
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Source Table</Form.Label>
            <Form.Control
              type="text"
              name="source_table"
              value={formData.source_table}
              onChange={handleChange}
              placeholder="e.g. AP_INVOICE_HEADER"
            />
            <Form.Text className="text-muted">
              The source system table this column maps to
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Source Column</Form.Label>
            <Form.Control
              type="text"
              name="source_column"
              value={formData.source_column}
              onChange={handleChange}
              placeholder="e.g. INVOICE_NUMBER"
            />
            <Form.Text className="text-muted">
              The source system column this maps to
            </Form.Text>
          </Form.Group>

          {supportsOptions && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="custom_options"
                label="Custom Options"
                checked={formData.custom_options}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Check if this column has custom options that need to be configured
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="me-2"
              >
                {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
              </Button>
              <Button
                type="button"
                variant="light-primary"
                onClick={onHide}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
            {isEditing && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                Delete
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SystemTableColumnModal;
